import { IApi } from '@/setup/api-client'
import { useApiQuery } from '@/utils/api/api-query'
import { IApiError } from '@/utils/api/api-types'
import { omit } from '@/utils/functions/omit'
import { UseQueryOptions } from '@tanstack/react-query'
import type { GetBrandData } from 'api/dist/routes/organization/[organization_id]/brands/[brand_slug]'
import { BRAND_QUERY_BASE_KEY } from './get-brands-query'
import { useApiToolbox } from '@/utils/api/use-api-toolbox'

export type IGetBrandQueryParams = {
    organizationId?: string
    brandSlug?: string
}

export type IBaseBrand = NonNullable<GetBrandData> & { slug: string }

export type IBrand<TExtended extends boolean = false> = TExtended extends true ? IBaseBrand : Omit<IBaseBrand, 'assets'>

export class GetBrandQuery {
    static getKey(params: IGetBrandQueryParams, extended: boolean) {
        return [
            ...BRAND_QUERY_BASE_KEY,
            'organization',
            params.organizationId,
            params.brandSlug,
            Boolean(extended),
            'get-brand-query',
        ]
    }

    static async call<TExtended extends boolean = false>(api: IApi, params: IGetBrandQueryParams, extended: TExtended) {
        return api
            .get<
                IBrand<TExtended>
            >(`/organization/${params.organizationId}/brands/${params.brandSlug}`, extended ? { extended: true } : {})
            .then((res) => res.data)
    }
}

export function useGetBrandQuery<TExtended extends boolean = false>(
    extended: TExtended,
    brandSlug?: string,
    options?: Omit<UseQueryOptions<IBrand<TExtended>, IApiError, IBrand<TExtended>>, 'queryKey' | 'queryFn'>
) {
    const { api, apiParams } = useApiToolbox()

    const computedBrandSlug = brandSlug || apiParams.brandSlug

    const payload = {
        organizationId: apiParams.organizationId,
        brandSlug: computedBrandSlug,
    }

    return useApiQuery<IBrand<TExtended>>({
        queryKey: GetBrandQuery.getKey(payload, extended),
        queryFn: () => GetBrandQuery.call<TExtended>(api, payload, extended),
        enabled: !!apiParams.organizationId && !!computedBrandSlug && (options?.enabled ?? true),
        // Seeded values (e.g. from create-empty-brand-mutation) must not be
        // marked stale on first mount or the destination page refetches into
        // the lagged replica.
        staleTime: 30 * 1000,
        ...omit(options || {}, 'enabled'),
    })
}
